import React from 'react';

import styled, { css } from 'styled-components';

import { isPreorderEligibleBySelectedProduct } from '~/shared/utils/isPreorderEligibleBySelectedProduct';
import {
  QuantitySelector as BaseQuantitySelector,
  useProductDetailContext,
  useProductDetailSelectionState,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const defaultQuantity = 5;

const QuantitySelector = styled(BaseQuantitySelector)`
  margin: 20px 0;
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
`;

const buttonWrapperStyle = css`
  border: 2px solid ${({ theme }) => theme.colors.pdpQuantitySelectorBorder};
  border-radius: 4px;
  height: 40px;
  width: min-content;
`;

const currentQuantityStyle = css`
  width: 100px;
  height: 100%;
  font-size: 14px;

  ${({ theme }) => theme.pdpStyles.quantitySelectors.currentQuantityStyle}
`;

const baseButton = css`
  background: none;
  box-shadow: none;
  padding: 0.75em 1em;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
  }

  ${({ theme }) => theme.pdpStyles.quantitySelectors.baseButton}
`;

const decrementButtonStyle = css`
  ${baseButton}
  border-radius: 4px 0px 0px 4px;
  border-right: 2px solid
    ${({ theme }) => theme.colors.pdpQuantitySelectorBorder};
`;

const incrementButtonStyle = css`
  ${baseButton}
  border-radius: 0px 4px 4px 0px;
  border-left: 2px solid
    ${({ theme }) => theme.colors.pdpQuantitySelectorBorder};
`;

const iconStyle = css`
  width: 10px;
  height: 10px;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const categoryLabelStyle = css`
  ${({ theme }) => theme.pdpStyles.quantitySelectors.indicator.label}
`;

const quantityTextStyle = css`
  ${({ theme }) => theme.pdpStyles.quantitySelectors.indicator.value}
`;

export default function ProductDetailQuantitySelector({ ...rest }) {
  const {
    itemQuantity,
    onQuantityChange,
    product,
    selectionStateByProductIndex,
  } = useProductDetailContext();
  const { selectedSKU } = useProductDetailSelectionState();
  const isProductAvailable = selectedSKU
    ? selectedSKU.availableQuantity > 0
    : false;
  const isProductPreorderAvailable = isPreorderEligibleBySelectedProduct(
    product,
    selectionStateByProductIndex
  );
  const availableQuantity = isProductPreorderAvailable
    ? selectedSKU?.availableQuantityPreorder
    : selectedSKU?.availableQuantity;

  // with no default size selected on PDP, `selectedSKU` = `null`, so `maxQuantity` = `defaultQuantity`
  // and the QuantitySelector is disabled
  const maxQuantity = selectedSKU
    ? Math.min(defaultQuantity, availableQuantity)
    : defaultQuantity;

  return (
    <QuantitySelector
      buttonWrapperStyle={buttonWrapperStyle}
      currentQuantityStyle={currentQuantityStyle}
      decrementButtonStyle={decrementButtonStyle}
      categoryLabelStyle={categoryLabelStyle}
      quantityTextStyle={quantityTextStyle}
      disabled={
        isProductPreorderAvailable
          ? !isProductPreorderAvailable
          : !isProductAvailable
      }
      iconStyle={iconStyle}
      incrementButtonStyle={incrementButtonStyle}
      initialValue={itemQuantity}
      label={
        <FormattedMessage
          id="site_product_page.abbreviated_quantity"
          defaultMessage="Qty"
        />
      }
      maxQuantity={maxQuantity}
      onChange={onQuantityChange}
      showQuantityNearLabel
      value={itemQuantity}
      {...rest}
    />
  );
}
